/**
 * MockTestPage — recognition check, not exam.
 *
 * URL: /journeys/:slug/mock        (full pool)
 *      /journeys/:slug/mock?day=N  (day-tagged pool only)
 *
 * Flow per question:
 *   1. Soft prompt + 4 calm option buttons.
 *   2. After tap, ask: "Did this feel familiar, mostly familiar, or not yet?"
 *   3. Hold the response, advance.
 *
 * Submit calls /mock/submit. Server returns recognition counts only — no
 * score, no percentage, no pass/fail. We display them as gentle copy:
 *   "X felt familiar.  Y mostly.  Z not yet — they'll soften."
 *
 * Designed to demonstrate the absorption philosophy: recognition over recall.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, Check, Sparkles } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_LABEL,
} from "../../components/journeys/journeyTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function MockTestPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const day = params.get("day");

  const [questions, setQuestions] = useState(null);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { qid: { selected_option, felt_familiar } }
  const [phase, setPhase] = useState("question"); // "question" | "recognition"
  const [submitting, setSubmitting] = useState(false);
  const [recognition, setRecognition] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = day
      ? `${API}/api/journeys/${slug}/mock/questions?day=${day}`
      : `${API}/api/journeys/${slug}/mock/questions`;
    fetch(url, { headers: auth() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { if (!cancelled) setQuestions(data.questions || []); })
      .catch(() => !cancelled && setError("Could not load"));
    return () => { cancelled = true; };
  }, [slug, day]);

  const total = questions?.length || 0;
  const q = questions?.[index];
  const progress = useMemo(
    () => (total > 0 ? Math.round(((index + (phase === "recognition" ? 0.5 : 0)) / total) * 100) : 0),
    [index, phase, total]
  );

  const pickOption = (option) => {
    setAnswers((prev) => ({
      ...prev,
      [q.id]: { ...(prev[q.id] || {}), selected_option: option },
    }));
    setPhase("recognition");
  };

  const pickRecognition = (felt_familiar) => {
    const next = { ...answers, [q.id]: { ...(answers[q.id] || {}), felt_familiar } };
    setAnswers(next);
    if (index + 1 < total) {
      setIndex(index + 1);
      setPhase("question");
    } else {
      submit(next);
    }
  };

  const submit = async (final) => {
    setSubmitting(true);
    try {
      const payload = {
        day: day ? parseInt(day, 10) : 0,
        answers: Object.entries(final).map(([question_id, v]) => ({
          question_id,
          selected_option: v.selected_option,
          felt_familiar: !!v.felt_familiar,
        })),
      };
      const r = await fetch(`${API}/api/journeys/${slug}/mock/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      setRecognition(data);
    } catch {
      setRecognition({ recognition: { familiar: 0, mostly: 0, not_yet: total, total }, message: "" });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render states ─────────────────────────────────────────────────
  if (error) {
    return (
      <Wrap>
        <p className="text-[14px]" style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD, fontStyle: "italic" }}>
          {error}
        </p>
      </Wrap>
    );
  }

  if (!questions) {
    return (
      <Wrap>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.accentBlue }} />
      </Wrap>
    );
  }

  if (questions.length === 0) {
    return (
      <Wrap>
        <BackBtn navigate={navigate} slug={slug} />
        <div className="max-w-md mx-auto text-center pt-12">
          <p
            className="text-[10px] uppercase tracking-[0.4em] mb-3"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
          >
            Recognition check
          </p>
          <h1
            className="text-[26px] italic mb-4"
            style={{ color: P.textPrimary, fontFamily: JOURNEY_FONT_HEAD, fontWeight: 400 }}
          >
            No questions yet.
          </h1>
          <p
            className="text-[14px] mb-10"
            style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD, fontStyle: "italic" }}
          >
            Mock test content is being prepared. Come back soon — or continue your daily flow in the meantime.
          </p>
        </div>
      </Wrap>
    );
  }

  if (recognition) {
    const { familiar = 0, mostly = 0, not_yet = 0 } = recognition.recognition || {};
    return (
      <Wrap>
        <BackBtn navigate={navigate} slug={slug} />
        <div className="max-w-md mx-auto text-center pt-12">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 inline-flex w-16 h-16 rounded-full items-center justify-center"
            style={{
              background: `radial-gradient(circle, #FAF7F0 0%, ${P.accentGold}55 80%)`,
              boxShadow: "0 0 30px rgba(168,137,92,0.35)",
            }}
          >
            <Check size={26} style={{ color: P.accentGold }} />
          </motion.div>
          <p
            className="text-[10px] uppercase tracking-[0.4em] mb-3"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
          >
            What's landed
          </p>
          <h1
            className="text-[28px] italic mb-8"
            style={{ color: P.textPrimary, fontFamily: JOURNEY_FONT_HEAD, fontWeight: 400 }}
          >
            {recognition.message || "Take what landed. The rest will soften."}
          </h1>

          <div className="space-y-3 max-w-xs mx-auto text-left">
            <RecognitionRow label="Felt familiar" count={familiar} accent={P.accentGold} />
            <RecognitionRow label="Mostly familiar" count={mostly} accent={P.accentBlue} />
            <RecognitionRow label="Not yet — they'll soften" count={not_yet} accent={P.textSecondary} />
          </div>

          <button
            onClick={() => navigate(`/journeys/${slug}`)}
            className="mt-12 rounded-full px-7 py-3"
            style={{
              background: `linear-gradient(135deg, ${P.accentBlue} 0%, ${P.accentBlueDeep} 100%)`,
              color: "#FAF7F0",
              fontFamily: JOURNEY_FONT_HEAD,
              fontSize: 14,
              border: "1px solid rgba(168,137,92,0.35)",
            }}
            data-testid="mock-finish-btn"
          >
            Return to the path
          </button>
        </div>
      </Wrap>
    );
  }

  // Question / recognition flow
  return (
    <Wrap>
      <BackBtn navigate={navigate} slug={slug} />
      <div className="max-w-md mx-auto pt-10 pb-24">
        {/* faint progress hint, never numeric */}
        <div
          className="h-[2px] rounded-full mb-12 overflow-hidden mx-8"
          style={{ background: "rgba(40,32,26,0.06)" }}
        >
          <div
            className="h-full rounded-full transition-[width] duration-700"
            style={{
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${P.accentBlue}, ${P.accentTeal})`,
            }}
          />
        </div>

        <p
          className="text-[10px] uppercase tracking-[0.4em] mb-4 text-center"
          style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
        >
          Recognition · {index + 1} of {total}
        </p>

        <AnimatePresence mode="wait">
          {phase === "question" && (
            <motion.div
              key={`q-${q.id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid="mock-question"
            >
              <p
                className="text-[22px] italic leading-snug mb-10 text-center px-2"
                style={{ color: P.textPrimary, fontFamily: JOURNEY_FONT_HEAD, fontWeight: 400 }}
              >
                {q.prompt}
              </p>

              <div className="space-y-3">
                {(q.options || []).map((opt, i) => (
                  <motion.button
                    key={opt}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                    whileTap={{ scale: 0.985 }}
                    onClick={() => pickOption(opt)}
                    className="w-full text-left p-4 rounded-2xl"
                    style={{
                      background: P.bgGlass,
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      border: `1px solid ${P.hairline}`,
                      color: P.textPrimary,
                      fontFamily: JOURNEY_FONT_HEAD,
                      fontSize: 15,
                      lineHeight: 1.5,
                    }}
                    data-testid={`mock-option-${i}`}
                  >
                    {opt}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {phase === "recognition" && (
            <motion.div
              key={`r-${q.id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid="mock-recognition-tap"
            >
              <p
                className="text-[18px] italic leading-snug mb-10 text-center"
                style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD, fontWeight: 400 }}
              >
                Did that one feel familiar?
              </p>

              <div className="space-y-3">
                <RecognitionTap
                  label="Felt familiar"
                  testid="mock-felt-familiar"
                  onClick={() => pickRecognition(true)}
                  primary
                />
                <RecognitionTap
                  label="Mostly"
                  testid="mock-felt-mostly"
                  onClick={() => pickRecognition(false)}
                />
                <RecognitionTap
                  label="Not yet"
                  testid="mock-felt-not-yet"
                  onClick={() => pickRecognition(false)}
                />
              </div>

              <p
                className="text-[10px] tracking-[0.3em] uppercase text-center mt-10"
                style={{ color: P.textMuted, fontFamily: JOURNEY_FONT_LABEL }}
              >
                no scores · just noticing
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {submitting && (
          <div className="text-center mt-8">
            <Loader2 className="w-5 h-5 animate-spin inline" style={{ color: P.accentBlue }} />
          </div>
        )}
      </div>
    </Wrap>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────
function Wrap({ children }) {
  return (
    <div
      className="min-h-screen px-6 pt-16 pb-12 flex flex-col"
      style={{ background: P.bgWash }}
      data-testid="mock-test-page"
    >
      {children}
    </div>
  );
}

function BackBtn({ navigate, slug }) {
  return (
    <button
      onClick={() => navigate(`/journeys/${slug}`)}
      className="fixed top-4 left-4 z-50 p-2 rounded-full"
      style={{ background: "rgba(255,253,247,0.6)", border: `1px solid ${P.hairline}` }}
      data-testid="mock-back-btn"
      aria-label="Back"
    >
      <ArrowLeft size={16} style={{ color: P.textSecondary }} />
    </button>
  );
}

function RecognitionTap({ label, onClick, testid, primary }) {
  return (
    <motion.button
      whileTap={{ scale: 0.985 }}
      onClick={onClick}
      data-testid={testid}
      className="w-full p-4 rounded-2xl flex items-center justify-center gap-2"
      style={{
        background: primary
          ? `linear-gradient(135deg, ${P.accentBlue}26 0%, ${P.accentBlue}0F 100%)`
          : P.bgGlass,
        border: `1px solid ${primary ? P.accentBlue + "55" : P.hairline}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        color: P.textPrimary,
        fontFamily: JOURNEY_FONT_HEAD,
        fontSize: 15,
        fontStyle: "italic",
      }}
    >
      {primary && <Sparkles size={14} style={{ color: P.accentBlue }} />}
      {label}
    </motion.button>
  );
}

function RecognitionRow({ label, count, accent }) {
  return (
    <div
      className="flex items-center justify-between rounded-xl p-3"
      style={{
        background: P.bgGlass,
        border: `1px solid ${P.hairline}`,
      }}
    >
      <span
        className="text-[13px] italic"
        style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD }}
      >
        {label}
      </span>
      <span
        className="text-[18px]"
        style={{ color: accent, fontFamily: JOURNEY_FONT_HEAD, fontWeight: 500 }}
      >
        {count}
      </span>
    </div>
  );
}
